<script>
	import { onMount } from 'svelte';
	import { PWA } from '$lib/pwa.js';

	// localStorage key for remembering dismissal. Re-prompt after the cooldown.
	const DISMISS_KEY = 'installPromptDismissedAt';
	const COOLDOWN_DAYS = 14;

	let visible = $state(false);
	let mode = $state(''); // 'android' (native prompt available) | 'ios' (manual instructions)
	let deferredPrompt = null; // captured beforeinstallprompt event (Android/desktop Chrome)

	function isStandalone() {
		return (
			window.matchMedia('(display-mode: standalone)').matches ||
			window.navigator.standalone === true
		);
	}

	function dismissedRecently() {
		const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
		if (!at) return false;
		const ageDays = (Date.now() - at) / (1000 * 60 * 60 * 24);
		return ageDays < COOLDOWN_DAYS;
	}

	function remember() {
		localStorage.setItem(DISMISS_KEY, String(Date.now()));
	}

	function close() {
		visible = false;
		remember();
	}

	async function install() {
		if (!deferredPrompt) return;
		deferredPrompt.prompt();
		await deferredPrompt.userChoice;
		deferredPrompt = null;
		close();
	}

	onMount(() => {
		// Stay quiet entirely until the install experience is publicly unveiled.
		if (!PWA.inviteInstall) return;

		// Never prompt if already installed or recently dismissed.
		if (isStandalone() || dismissedRecently()) return;

		// Android / desktop Chrome: capture the native install event.
		const onBeforeInstall = (e) => {
			e.preventDefault();
			deferredPrompt = e;
			mode = 'android';
			visible = true;
		};
		window.addEventListener('beforeinstallprompt', onBeforeInstall);

		// Hide and stop re-prompting once installed.
		const onInstalled = () => {
			remember();
			visible = false;
		};
		window.addEventListener('appinstalled', onInstalled);

		// iOS Safari has no install event — detect it and show manual steps.
		const ua = window.navigator.userAgent;
		const isIOS = /iphone|ipad|ipod/i.test(ua);
		const isSafari = /^((?!chrome|crios|fxios|edgios).)*safari/i.test(ua);
		if (isIOS && isSafari) {
			mode = 'ios';
			// Brief delay so it doesn't compete with first paint.
			setTimeout(() => {
				if (!isStandalone()) visible = true;
			}, 2500);
		}

		return () => {
			window.removeEventListener('beforeinstallprompt', onBeforeInstall);
			window.removeEventListener('appinstalled', onInstalled);
		};
	});
</script>

{#if visible}
	<div
		class="fixed inset-x-0 bottom-[calc(60px+env(safe-area-inset-bottom))] z-40 px-3 md:bottom-4 md:left-auto md:right-4 md:w-96 md:px-0"
		role="dialog"
		aria-label="Install St. Thomas app"
		style="animation: slideUp 250ms ease-out"
	>
		<div class="rounded-2xl border border-stone-dark bg-altar-white p-4 shadow-lg">
			<div class="flex items-start gap-3">
				<img src={PWA.icons[0].src} alt="" class="h-11 w-11 flex-shrink-0 rounded-xl" />
				<div class="min-w-0 flex-1">
					<p class="font-heading text-base font-semibold text-navy">Add to your Home Screen</p>
					{#if mode === 'android'}
						<p class="mt-0.5 text-sm text-navy-muted">
							Install St. Thomas for quick, app-like access.
						</p>
						<div class="mt-3 flex gap-2">
							<button
								onclick={install}
								class="rounded-full bg-wine px-4 py-1.5 text-sm font-medium text-parchment transition-colors hover:bg-wine-dark"
							>
								Install
							</button>
							<button
								onclick={close}
								class="rounded-full px-4 py-1.5 text-sm font-medium text-navy-muted transition-colors hover:text-navy"
							>
								Not now
							</button>
						</div>
					{:else}
						<p class="mt-1 text-sm leading-relaxed text-navy-muted">
							Tap
							<svg
								class="mx-0.5 inline h-4 w-4 align-text-bottom text-wine"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								stroke-width="1.8"
								aria-label="Share"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="M12 4v12m0-12L8 8m4-4l4 4M6 12v6a2 2 0 002 2h8a2 2 0 002-2v-6"
								/>
							</svg>
							in the toolbar, then choose
							<span class="font-medium text-navy">Add to Home Screen</span>.
						</p>
						<button
							onclick={close}
							class="mt-3 rounded-full px-4 py-1.5 text-sm font-medium text-navy-muted transition-colors hover:text-navy"
						>
							Got it
						</button>
					{/if}
				</div>
				<button
					onclick={close}
					class="-mr-1 -mt-1 flex-shrink-0 p-1 text-navy-muted transition-colors hover:text-navy"
					aria-label="Dismiss"
				>
					<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
						<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
		</div>
	</div>
{/if}

<style>
	@keyframes slideUp {
		from {
			transform: translateY(120%);
			opacity: 0;
		}
		to {
			transform: translateY(0);
			opacity: 1;
		}
	}
</style>
